<template>
  <div class="notes-widget-container">
    <!-- Widget-Steuerelemente für Notizen -->
    <div class="widget-controls mb-3">

      <!-- Filter-Optionen -->
      <div class="filter-controls ml-auto d-flex align-center">
        <v-btn-toggle
          v-model="filterOption"
          density="comfortable"
          color="primary"
          rounded
          class="filter-toggle"
          mandatory
        >
          <v-btn value="all" variant="text" size="small" class="filter-btn">
            <v-icon size="small">mdi-note-multiple</v-icon>
            <span class="d-none d-sm-inline ml-1">Alle</span>
          </v-btn>
          <v-btn value="pinned" variant="text" size="small" class="filter-btn">
            <v-icon size="small">mdi-pin</v-icon>
            <span class="d-none d-sm-inline ml-1">Angeheftet</span>
          </v-btn>
        </v-btn-toggle>
      </div>
    </div>

    <!-- Notiz-Listendarstellung -->
    <v-card-text class="card-content">
      <div v-if="displayedNotes.length > 0" class="notes-list">
        <div
          v-for="note in displayedNotes"
          :key="note.id"
          class="note-card-wrapper mb-3"
        >
          <v-card
            class="note-card"
            :class="{ 'pinned-note': note.pinned }"
            :style="{ borderLeftColor: note.color }"
            @click="editNote(note)"
          >
            <div class="note-header d-flex align-center">
              <h3 class="note-title text-truncate">
                {{ note.title || 'Unbenannte Notiz' }}
              </h3>

              <div class="note-actions ml-auto d-flex">
                <v-btn
                  icon
                  size="x-small"
                  color="primary"
                  variant="text"
                  @click.stop="togglePin(note)"
                  :title="note.pinned ? 'Anheften aufheben' : 'Anheften'"
                >
                  <v-icon>
                    {{ note.pinned ? 'mdi-pin' : 'mdi-pin-outline' }}
                  </v-icon>
                </v-btn>

                <v-btn
                  icon
                  size="x-small"
                  color="error"
                  variant="text"
                  @click.stop="confirmDeleteNote(note)"
                  title="Löschen"
                >
                  <v-icon>mdi-delete-outline</v-icon>
                </v-btn>
              </div>
            </div>

            <div class="note-preview">
              <div v-html="sanitizeNoteHtml(note.content)"></div>
            </div>

            <div class="note-footer d-flex align-center text-caption text-disabled">
              <span>{{ formatDate(note.updated_at) }}</span>
            </div>
          </v-card>
        </div>
      </div>

      <!-- Leere Zustandsanzeige -->
      <div v-else class="empty-state text-center py-6">
        <v-icon size="40" color="primary" class="mb-2">mdi-note-text-outline</v-icon>
        <h3 class="empty-title text-h6 mb-2">Keine Notizen</h3>
        <p class="empty-desc text-body-2 text-medium-emphasis mb-4">
          {{ filterOption === 'pinned' ? 'Du hast noch keine Notizen angeheftet.' : 'Erstelle deine erste Notiz, um loszulegen.' }}
        </p>
        <v-btn
          v-if="filterOption === 'pinned' && allNotes.length > 0"
          variant="text"
          color="primary"
          @click="filterOption = 'all'"
          size="small"
          class="mb-2"
        >
          Alle Notizen anzeigen
        </v-btn>
        <v-btn
          v-else
          color="primary"
          variant="elevated"
          @click="createNewNote"
          prepend-icon="mdi-plus"
          size="small"
        >
          Erste Notiz erstellen
        </v-btn>
      </div>

      <!-- Mehr anzeigen Button -->
      <div class="d-flex justify-end mt-2" v-if="showMoreButton">
        <v-btn
          text
          size="small"
          variant="text"
          color="primary"
          class="show-more-button"
          @click="toggleShowAllNotes"
        >
          <v-icon size="small" class="mr-1">
            {{ showAllNotes ? "mdi-chevron-up" : "mdi-chevron-down" }}
          </v-icon>
          {{ showAllNotes ? "Weniger anzeigen" : "Alle anzeigen" }}
        </v-btn>
      </div>
    </v-card-text>

    <!-- Dialog zum Suchen von Notizen -->
    <v-dialog v-model="searchDialog" max-width="500">
      <v-card class="dialog-card search-dialog">
        <v-card-title class="dialog-title search-dialog-title">
          <v-icon color="primary" class="mr-2">mdi-note-search</v-icon>
          <span class="title-text">Notizen durchsuchen</span>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content">
          <v-text-field
            v-model="searchQuery"
            label="Suche nach Titel oder Inhalt"
            variant="outlined"
            color="primary"
            bg-color="rgba(30, 30, 40, 0.5)"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            autofocus
            class="search-input form-field"
          ></v-text-field>

          <v-list
            v-if="searchResults.length > 0"
            class="search-results mt-4"
          >
            <v-list-item
              v-for="note in searchResults"
              :key="note.id"
              class="search-result-item"
              rounded
              @click="editNote(note)"
            >
              <div class="d-flex align-center justify-space-between w-100">
                <div class="note-info flex-grow-1">
                  <div class="d-flex align-center">
                    <div
                      class="color-dot mr-2"
                      :style="{ backgroundColor: note.color }"
                    ></div>
                    <v-list-item-title class="font-weight-medium">
                      {{ note.title || 'Unbenannte Notiz' }}
                    </v-list-item-title>
                    <v-icon
                      v-if="note.pinned"
                      size="x-small"
                      color="grey"
                      class="ml-2"
                    >mdi-pin</v-icon>
                  </div>
                  <v-list-item-subtitle
                    v-if="note.content"
                    class="text-truncate"
                  >
                    {{ stripHtml(note.content) }}
                  </v-list-item-subtitle>
                </div>
                <div class="note-actions">
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    color="primary"
                    @click.stop="editNote(note)"
                    title="Notiz bearbeiten"
                  >
                    <v-icon size="small">mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-list-item>
          </v-list>

          <div
            v-else-if="searchQuery"
            class="text-center empty-results py-4"
          >
            <v-icon size="40" color="grey" class="mb-2">mdi-note-off</v-icon>
            <div class="text-grey">Keine Notizen gefunden</div>
          </div>

          <div
            v-else
            class="text-center search-placeholder py-4 text-medium-emphasis"
          >
            Geben Sie einen Suchbegriff ein, um Notizen zu finden
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions search-dialog-actions">
          <v-btn
            variant="text"
            prepend-icon="mdi-plus"
            color="primary"
            @click="createNewNoteFromSearch"
            class="add-btn"
          >
            Neue Notiz
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            variant="text"
            @click="closeSearchDialog"
          >
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog zum Bearbeiten einer Notiz -->
    <v-dialog v-model="editDialog" max-width="600" persistent>
      <v-card class="dialog-card note-dialog">
        <v-card-title class="dialog-title d-flex align-center justify-space-between">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2">
              {{ currentNote.id ? 'mdi-note-edit' : 'mdi-note-plus' }}
            </v-icon>
            <span class="title-text">
              {{ currentNote.id ? 'Notiz bearbeiten' : 'Neue Notiz' }}
            </span>
          </div>

          <div class="color-selector d-flex ml-2">
            <v-tooltip
              v-for="color in noteColors"
              :key="color.value"
              :text="color.name"
              location="bottom"
            >
              <template v-slot:activator="{ props }">
                <div
                  v-bind="props"
                  class="color-option"
                  :style="{ backgroundColor: color.value }"
                  :class="{ active: currentNote.color === color.value }"
                  @click="currentNote.color = color.value"
                ></div>
              </template>
            </v-tooltip>
          </div>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pt-4">
          <v-form @submit.prevent="saveNote">
            <v-text-field
              v-model="currentNote.title"
              label="Titel"
              variant="outlined"
              color="primary"
              bg-color="rgba(30, 30, 40, 0.5)"
              hide-details="auto"
              class="form-field mb-4"
              :style="{ borderLeft: `3px solid ${currentNote.color}` }"
            ></v-text-field>

            <!-- Rich-Text-Editor mit Quill -->
            <div class="quill-editor-container">
              <quill-editor
                v-model:content="currentNote.content"
                :toolbar="[
                  ['bold', 'italic', 'underline'],
                  [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                  ['link', 'clean']
                ]"
                theme="snow"
                contentType="html"
                placeholder="Inhalt hier eingeben..."
                class="quill-editor"
              />
            </div>

            <div class="d-flex align-center mt-3">
              <v-checkbox
                v-model="currentNote.pinned"
                label="Angeheftet"
                hide-details
                density="compact"
                color="primary"
              ></v-checkbox>

              <div class="last-edited ml-auto text-caption text-disabled" v-if="currentNote.updated_at">
                Zuletzt bearbeitet: {{ formatDate(currentNote.updated_at) }}
              </div>
            </div>
          </v-form>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn
            v-if="currentNote.id"
            variant="text"
            color="error"
            @click="confirmDeleteNote(currentNote)"
            prepend-icon="mdi-delete"
            class="delete-btn"
          >
            Löschen
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
            variant="text"
            @click="closeEditor"
            :disabled="loading"
          >
            Abbrechen
          </v-btn>

          <v-btn
            color="primary"
            variant="elevated"
            @click="saveNote"
            :loading="loading"
            class="submit-btn px-4"
          >
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bestätigungsdialog für das Löschen -->
    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card class="dialog-card confirm-dialog">
        <v-card-title class="dialog-title warning-header">
          <v-icon color="error" class="mr-2">mdi-alert</v-icon>
          <span class="title-text">Notiz löschen?</span>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pt-3">
          Bist du sicher, dass du diese Notiz löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>

          <v-btn
            variant="text"
            @click="deleteDialog = false"
            :disabled="loading"
          >
            Abbrechen
          </v-btn>

          <v-btn
            color="error"
            variant="elevated"
            @click="deleteNote"
            :loading="loading"
            class="px-4"
          >
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useToast } from 'vue-toastification';
import { API_BASE_URL } from '@/config';
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

// Toast-Benachrichtigungen
const toast = useToast();

// Zustand der Notizen
const allNotes = ref([]);
const loading = ref(false);
const editDialog = ref(false);
const deleteDialog = ref(false);
const searchDialog = ref(false);
const noteToDelete = ref(null);
const filterOption = ref('all');
const searchQuery = ref('');
const showAllNotes = ref(false);

// Konstante für die Dashboard-Anzeige
const MAX_DASHBOARD_NOTES = 3;

// Aktuelle Notiz für Bearbeitung/Erstellung
const currentNote = ref({
  id: null,
  title: '',
  content: '',
  color: '#4CAF50',
  pinned: false
});

// Verfügbare Farben für Notizen
const noteColors = [
  { name: 'Grün', value: '#4CAF50' },
  { name: 'Blau', value: '#2196F3' },
  { name: 'Rot', value: '#F44336' },
  { name: 'Orange', value: '#FF9800' },
  { name: 'Lila', value: '#9C27B0' },
  { name: 'Türkis', value: '#009688' },
  { name: 'Grau', value: '#607D8B' },
  { name: 'Pink', value: '#E91E63' }
];

// Gefilterte Notizen basierend auf Filter-Option
const filteredNotes = computed(() => {
  if (filterOption.value === 'pinned') {
    return allNotes.value.filter(note => note.pinned);
  }
  return allNotes.value;
});

// Notizen für das Dashboard - entweder alle oder begrenzte Anzahl
const displayedNotes = computed(() => {
  if (showAllNotes.value) {
    return filteredNotes.value;
  }
  return filteredNotes.value.slice(0, MAX_DASHBOARD_NOTES);
});

// Anzeigen des "Mehr anzeigen"-Buttons, wenn nötig
const showMoreButton = computed(() => {
  return filteredNotes.value.length > MAX_DASHBOARD_NOTES;
});

// Suchergebnisse
const searchResults = computed(() => {
  if (!searchQuery.value) return [];

  const query = searchQuery.value.toLowerCase();
  return allNotes.value.filter(note =>
    (note.title && note.title.toLowerCase().includes(query)) ||
    (note.content && stripHtml(note.content).toLowerCase().includes(query))
  );
});

// HTML-Tags aus Text entfernen für die Suche
function sanitizeNoteHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(String(html), {
    USE_PROFILES: { html: true },
  });
}

function stripHtml(html) {
  if (!html) return '';
  return html.replace(/<\/?[^>]+(>|$)/g, ' ').trim();
}

// Datum formatieren
function formatDate(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  const formattedTime = date.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  });

  if (diffDays === 0) {
    return `Heute, ${formattedTime}`;
  } else if (diffDays === 1) {
    return `Gestern, ${formattedTime}`;
  } else {
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + `, ${formattedTime}`;
  }
}

// Neue Notiz erstellen
function createNewNote() {
  currentNote.value = {
    id: null,
    title: '',
    content: '',
    color: '#4CAF50',
    pinned: false
  };
  editDialog.value = true;
}

// Notiz aus dem Suchdialog erstellen und Suchdialog schließen
function createNewNoteFromSearch() {
  searchDialog.value = false;
  searchQuery.value = '';
  createNewNote();
}

// Bestehende Notiz bearbeiten
function editNote(note) {
  currentNote.value = { ...note };
  editDialog.value = true;
}

// Editor schließen
function closeEditor() {
  editDialog.value = false;
}

// Suchdialog schließen
function closeSearchDialog() {
  searchDialog.value = false;
  searchQuery.value = '';
}

// Notiz speichern (erstellen oder aktualisieren)
async function saveNote() {
  if (!currentNote.value.content && !currentNote.value.title) {
    toast.info('Bitte füge einen Titel oder Inhalt hinzu');
    return;
  }

  loading.value = true;

  try {
    const action = currentNote.value.id ? 'update' : 'create';
    const response = await axios.post(`${API_BASE_URL}notes.php`, {
      action,
      ...currentNote.value,
      content: sanitizeNoteHtml(currentNote.value.content),
      title: String(currentNote.value.title || '').slice(0, 500),
    }, { withCredentials: true });

    if (response.data.status === 'success') {
      if (action === 'create') {
        allNotes.value.unshift(response.data.note);
        toast.success('Notiz erstellt');
      } else {
        const index = allNotes.value.findIndex(note => note.id === currentNote.value.id);
        if (index !== -1) {
          allNotes.value[index] = response.data.note;
        }
        toast.success('Notiz aktualisiert');
      }

      // Notizen sortieren - angeheftet zuerst, dann nach letzter Aktualisierung
      sortNotes();
      closeEditor();
    } else {
      toast.error(response.data.message || 'Fehler beim Speichern der Notiz');
    }
  } catch (error) {
    console.error('Fehler beim Speichern der Notiz:', error);
    toast.error('Ein Fehler ist aufgetreten');
  } finally {
    loading.value = false;
  }
}

// Notizen sortieren
function sortNotes() {
  allNotes.value.sort((a, b) => {
    // Erst nach Anheften-Status sortieren
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;

    // Dann nach Aktualisierungsdatum (neueste zuerst)
    return new Date(b.updated_at) - new Date(a.updated_at);
  });
}

// Löschen einer Notiz bestätigen
function confirmDeleteNote(note) {
  noteToDelete.value = note;
  deleteDialog.value = true;

  // Wenn der Bearbeitungsdialog aktiv ist, bleibt er offen
  // Ansonsten wird der Löschdialog separat angezeigt
}

// Notiz tatsächlich löschen
async function deleteNote() {
  if (!noteToDelete.value) return;

  loading.value = true;

  try {
    const response = await axios.post(`${API_BASE_URL}notes.php`, {
      action: 'delete',
      id: noteToDelete.value.id
    }, { withCredentials: true });

    if (response.data.status === 'success') {
      allNotes.value = allNotes.value.filter(note => note.id !== noteToDelete.value.id);
      toast.success('Notiz gelöscht');

      // Wenn die gelöschte Notiz die aktuell bearbeitete war, schließe den Editor
      if (editDialog.value && currentNote.value.id === noteToDelete.value.id) {
        editDialog.value = false;
      }
    } else {
      toast.error(response.data.message || 'Fehler beim Löschen der Notiz');
    }
  } catch (error) {
    console.error('Fehler beim Löschen der Notiz:', error);
    toast.error('Ein Fehler ist aufgetreten');
  } finally {
    loading.value = false;
    deleteDialog.value = false;
    noteToDelete.value = null;
  }
}

// Angeheftet-Status umschalten
async function togglePin(note) {
  try {
    const response = await axios.post(`${API_BASE_URL}notes.php`, {
      action: 'toggle_pin',
      id: note.id
    }, { withCredentials: true });

    if (response.data.status === 'success') {
      const index = allNotes.value.findIndex(n => n.id === note.id);
      if (index !== -1) {
        allNotes.value[index] = response.data.note;
        // Notizen neu sortieren
        sortNotes();
      }
    } else {
      toast.error(response.data.message || 'Fehler beim Ändern des Anheften-Status');
    }
  } catch (error) {
    console.error('Fehler beim Umschalten des Anheften-Status:', error);
    toast.error('Ein Fehler ist aufgetreten');
  }
}

// Umschalten der "Alle anzeigen"-Ansicht
function toggleShowAllNotes() {
  showAllNotes.value = !showAllNotes.value;
}

// Notizen laden
async function loadNotes() {
  loading.value = true;

  try {
    const response = await axios.get(`${API_BASE_URL}notes.php?fetch=1`, {
      withCredentials: true
    });

    if (response.data.status === 'success') {
      allNotes.value = response.data.notes || [];
      // Notizen sortieren beim Laden
      sortNotes();
    } else {
      console.error('Fehler beim Laden der Notizen:', response.data.message);
      toast.error('Notizen konnten nicht geladen werden');
    }
  } catch (error) {
    console.error('Fehler beim Abrufen der Notizen:', error);
    toast.error('Notizen konnten nicht geladen werden');
  } finally {
    loading.value = false;
  }
}

// Event-Handling für die Dashboard-Integration
import { inject } from 'vue';

// Suche und Hinzufügen-Dialog über inject abrufen
const showNotesSearchDialog = inject('showNotesSearchDialog', ref(false));
const showAddNoteDialog = inject('showAddNoteDialog', ref(false));

// Beobachten und auf Änderungen reagieren
watch(showNotesSearchDialog, (newVal) => {
  if (newVal === true) {
    searchDialog.value = true;
    nextTick(() => {
      showNotesSearchDialog.value = false;
    });
  }
});

watch(showAddNoteDialog, (newVal) => {
  if (newVal === true) {
    createNewNote();
    nextTick(() => {
      showAddNoteDialog.value = false;
    });
  }
});

// Bei Komponenteninitialisierung Notizen laden
onMounted(() => {
  loadNotes();
});
</script>

<style>
/* Quill Editor Anpassungen */
.quill-editor-container {
  margin-bottom: 16px;
}

.quill-editor {
  background-color: rgba(30, 30, 40, 0.5);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.9);
}

.ql-toolbar.ql-snow {
  background-color: rgba(35, 35, 45, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  border-bottom: none;
}

.ql-container.ql-snow {
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  border-top: none;
  min-height: 150px;
}

.ql-editor {
  min-height: 150px;
}

.ql-snow .ql-stroke {
  stroke: rgba(255, 255, 255, 0.7);
}

.ql-snow .ql-fill {
  fill: rgba(255, 255, 255, 0.7);
}

.ql-snow .ql-picker {
  color: rgba(255, 255, 255, 0.7);
}

.ql-snow.ql-toolbar button:hover,
.ql-snow .ql-toolbar button:hover,
.ql-snow.ql-toolbar button.ql-active,
.ql-snow .ql-toolbar button.ql-active,
.ql-snow.ql-toolbar .ql-picker-label:hover,
.ql-snow .ql-toolbar .ql-picker-label:hover,
.ql-snow.ql-toolbar .ql-picker-item:hover,
.ql-snow .ql-toolbar .ql-picker-item:hover {
  color: var(--v-primary-base);
}

.ql-snow.ql-toolbar button:hover .ql-stroke,
.ql-snow .ql-toolbar button:hover .ql-stroke,
.ql-snow.ql-toolbar button.ql-active .ql-stroke,
.ql-snow .ql-toolbar button.ql-active .ql-stroke {
  stroke: var(--v-primary-base);
}

.ql-snow.ql-toolbar button:hover .ql-fill,
.ql-snow .ql-toolbar button:hover .ql-fill,
.ql-snow.ql-toolbar button.ql-active .ql-fill,
.ql-snow .ql-toolbar button.ql-active .ql-fill {
  fill: var(--v-primary-base);
}

/* Notiz-Inhalt Darstellung */
.note-preview {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  max-height: 100px;
  overflow: hidden;
  flex: 1;
  margin-bottom: 8px;
  line-height: 1.4;
}

.note-preview ul, .note-preview ol {
  padding-left: 20px;
  margin: 8px 0;
}

.note-preview li {
  margin-bottom: 4px;
}

.note-preview a {
  color: #2196F3;
  text-decoration: none;
}

.note-preview a:hover {
  text-decoration: underline;
}

.note-preview strong {
  font-weight: bold;
}

.note-preview em {
  font-style: italic;
}

.note-preview u {
  text-decoration: underline;
}
</style>

<style scoped>
/* ======== Notizen-Widget Container ======== */
.card-content {
  padding: 16px;
}

/* ======== Notizen-Liste ======== */
.notes-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}

.note-card {
  position: relative;
  cursor: pointer;
  background: rgba(30, 30, 40, 0.7) !important;
  border-radius: 12px !important;
  border-left: 4px solid;
  padding: 14px;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  overflow: hidden;
}

.note-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
}

.pinned-note {
  background: linear-gradient(
    135deg,
    rgba(40, 40, 50, 0.9),
    rgba(30, 30, 40, 0.8)
  ) !important;
}

.pinned-note::before {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 24px;
  height: 24px;
  background-color: #2196F3;
  clip-path: polygon(100% 0, 0 0, 100% 100%);
}

.note-header {
  margin-bottom: 8px;
}

.note-title {
  font-size: 0.95rem;
  font-weight: 500;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.note-actions {
  opacity: 0.3;
  transition: opacity 0.2s ease;
}

.note-card:hover .note-actions {
  opacity: 1;
}

.note-footer {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

/* ======== Leerer Zustand ======== */
.empty-state {
  background: rgba(30, 30, 40, 0.3);
  border-radius: 12px;
  padding: 24px;
}

.empty-title {
  color: rgba(255, 255, 255, 0.9);
}

.empty-desc {
  max-width: 280px;
  margin: 0 auto;
}

/* ======== Editor Dialog ======== */
.note-dialog {
  background: rgba(25, 25, 30, 0.9) !important;
}

.dialog-title {
  padding: 16px 20px;
  border-bottom: none;
  position: relative;
}

.dialog-divider {
  border-color: rgba(255, 255, 255, 0.06);
  margin: 0;
}

.dialog-content {
  padding: 20px;
  color: rgba(255, 255, 255, 0.9);
}

.dialog-actions {
  padding: 12px 20px;
  background: rgba(20, 20, 30, 0.3);
}

.warning-header {
  background: linear-gradient(
    90deg,
    rgba(244, 67, 54, 0.2),
    rgba(244, 67, 54, 0.1),
    transparent
  );
}

.color-selector {
  display: flex;
  gap: 4px;
}

.color-option {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.color-option:hover {
  transform: scale(1.2);
}

.color-option.active {
  border: 2px solid #ffffff;
  transform: scale(1.1);
}

.last-edited {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
}

/* ======== Suchergebnisse ======== */
.search-dialog {
  background: rgba(25, 25, 30, 0.9) !important;
}

.search-input {
  margin-bottom: 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.search-results {
  max-height: 350px;
  overflow-y: auto;
  background: rgba(30, 30, 40, 0.3);
  border-radius: 8px;
  padding: 4px;
  border: 0px solid rgba(255, 255, 255, 0.05);
}

.search-result-item {
  margin-bottom: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  background: rgba(30, 30, 40, 0.5);
  backdrop-filter: blur(5px);
  -webkit-backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.search-result-item:hover {
  background-color: rgba(35, 35, 45, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.empty-results {
  background: rgba(30, 30, 40, 0.3);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.search-placeholder {
  background: rgba(30, 30, 40, 0.3);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

/* ======== Mehr anzeigen Button ======== */
.show-more-button {
  font-size: 0.85rem;
  opacity: 0.9;
  transition: all 0.3s ease;
}

.show-more-button:hover {
  opacity: 1;
  transform: translateY(-1px);
}

.show-more-button .v-icon {
  transition: transform 0.3s ease;
}

.show-more-button:hover .v-icon {
  transform: translateY(-2px);
}

/* Media Queries für responsive Darstellung */
@media (max-width: 768px) {
  .notes-list {
    grid-template-columns: 1fr;
  }
}
</style>
